import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Modal, Tooltip } from "antd";
import { svg } from "../../../../Assets/svg";
import { useAppSelector } from "../../../../store";
import { selectQuotationDetails, selectCartItems } from "../quotationSlice";
import { formatCurrency } from "../../../../helpers/utils";
import { BtnLoader } from "../../../../Components/Components";

interface MarginDiscountModalProps {
  open: boolean;
  onCancel: () => void;
  onApply: (margin: number, discount: number) => void;
  initialMargin: number | any;
  initialDiscount: number | any;
  maxDiscount?: number;
  maxMarkup?: number;
  quotationDetails?: any;
  cartItems?: any[];
  step?: number;
  isLoading?: any;
  vatPercentage?: number | string | null;
  vatType?: string | null;
  vatNumber?: string | null;
}

// ============================================================================
// CONFIGURATION CONSTANTS
// Change the step value in ONE place here (e.g. 5, 10, etc.):
// ============================================================================
export const DEFAULT_RULER_STEP = 10;

export const MarginDiscountModal: React.FC<MarginDiscountModalProps> = ({
  open,
  onCancel,
  onApply,
  initialMargin,
  initialDiscount,
  maxDiscount = 0,
  maxMarkup = 100,
  quotationDetails: propQuotationDetails,
  cartItems: propCartItems,
  step = DEFAULT_RULER_STEP,
  isLoading = false,
  vatPercentage: propVatPercentage,
  vatType,
  vatNumber,
}) => {
  // Configurable single step variable (uses step prop if provided, else DEFAULT_RULER_STEP)
  const rulerStep = Math.max(1, Number(step || DEFAULT_RULER_STEP));

  // Redux state fallback
  const reduxQuotationDetails = useAppSelector(selectQuotationDetails);
  const reduxCartItems = useAppSelector(selectCartItems);

  const activeQuotationDetails = propQuotationDetails || reduxQuotationDetails;
  const activeCartItems = propCartItems || reduxCartItems;

  const currency = activeQuotationDetails?.currency || "GBP";

  // Positive value = Markup / Margin (0 to maxMarkup)
  // Negative value = Discount (-maxDiscountLimit to 0)
  // 0 = Neither
  const [value, setValue] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const rulerContainerRef = useRef<HTMLDivElement | null>(null);

  // Dynamic limits
  const scaleMaxMarkup = Math.max(
    100,
    Number(maxMarkup || 100),
    Number(initialMargin || 0),
  );
  const scaleMaxDiscount = Math.max(
    Number(maxDiscount || 0),
    Number(initialDiscount || 0),
  );

  // Initialize or reset value when modal opens
  useEffect(() => {
    if (open) {
      const numMargin = Number(initialMargin);
      const numDiscount = Number(initialDiscount);
      if (!isNaN(numMargin) && numMargin > 0) {
        setValue(Math.min(scaleMaxMarkup, numMargin));
      } else if (!isNaN(numDiscount) && numDiscount > 0) {
        setValue(-Math.min(scaleMaxDiscount, numDiscount));
      } else {
        setValue(0);
      }
    }
  }, [open, initialMargin, initialDiscount, scaleMaxMarkup, scaleMaxDiscount]);

  // Geometry dimensions optimized for side-by-side layout
  const svgWidth = 180;
  const svgHeight = 450;
  const centerX = 82;

  // Vertical placement
  const rulerTopY = 70;
  const totalUnits = scaleMaxMarkup + scaleMaxDiscount;
  const rulerHeight = 310;
  const unitHeight = rulerHeight / totalUnits;

  // The 0 tick position
  const zeroY = rulerTopY + scaleMaxMarkup * unitHeight;
  const rulerBottomY = zeroY + scaleMaxDiscount * unitHeight;

  // Convert value (-scaleMaxDiscount to +scaleMaxMarkup) to SVG Y coordinate
  const getYFromValue = useCallback(
    (val: number) => {
      if (val >= 0) {
        return zeroY - val * unitHeight;
      } else {
        return zeroY + Math.abs(val) * unitHeight;
      }
    },
    [zeroY, unitHeight],
  );

  // Convert SVG Y coordinate to value
  const getValueFromY = useCallback(
    (svgY: number) => {
      if (svgY <= zeroY) {
        const raw = (zeroY - svgY) / unitHeight;
        const clamped = Math.min(scaleMaxMarkup, Math.max(0, Math.round(raw)));
        return clamped;
      } else {
        const raw = (svgY - zeroY) / unitHeight;
        const clamped = Math.min(
          scaleMaxDiscount,
          Math.max(0, Math.round(raw)),
        );
        return -clamped;
      }
    },
    [zeroY, unitHeight, scaleMaxMarkup, scaleMaxDiscount],
  );

  // Convert mouse/touch clientY to SVG Y
  const getSvgYFromClientY = useCallback(
    (clientY: number) => {
      if (!svgRef.current) return zeroY;
      const rect = svgRef.current.getBoundingClientRect();
      const scale = svgHeight / rect.height;
      return (clientY - rect.top) * scale;
    },
    [svgHeight, zeroY],
  );

  const isDraggingRef = useRef(false);

  // Handle Dragging via Mouse
  const handlePointerDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    const svgY = getSvgYFromClientY(e.clientY);
    setValue(getValueFromY(svgY));
  };

  // Attach non-passive touch listeners to the ruler container so mobile browsers do not initiate page scrolling
  useEffect(() => {
    const container = rulerContainerRef.current;
    if (!container || !open) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
      isDraggingRef.current = true;
      setIsDragging(true);
      if (e.touches && e.touches.length > 0) {
        const clientY = e.touches[0].clientY;
        const svgY = getSvgYFromClientY(clientY);
        setValue(getValueFromY(svgY));
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
      if (!isDraggingRef.current) return;
      if (e.touches && e.touches.length > 0) {
        const clientY = e.touches[0].clientY;
        const svgY = getSvgYFromClientY(clientY);
        setValue(getValueFromY(svgY));
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, [open, getSvgYFromClientY, getValueFromY]);

  useEffect(() => {
    const handlePointerMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      if (e.cancelable) {
        e.preventDefault();
      }
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const svgY = getSvgYFromClientY(clientY);
      setValue(getValueFromY(svgY));
    };

    const handlePointerUp = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handlePointerMove);
      window.addEventListener("mouseup", handlePointerUp);
      window.addEventListener("touchmove", handlePointerMove, {
        passive: false,
      });
      window.addEventListener("touchend", handlePointerUp);
      window.addEventListener("touchcancel", handlePointerUp);
    }

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
      window.removeEventListener("touchcancel", handlePointerUp);
    };
  }, [isDragging, getSvgYFromClientY, getValueFromY]);

  // Handle Wheel Scrolling over the ruler container
  useEffect(() => {
    const container = rulerContainerRef.current;
    if (!container || !open) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Scrolling up (deltaY < 0) moves pointer UP (towards markup)
      // Scrolling down (deltaY > 0) moves pointer DOWN (towards discount)
      const scrollStep = e.shiftKey ? rulerStep : 1;
      if (e.deltaY < 0) {
        setValue((prev) => Math.min(scaleMaxMarkup, prev + scrollStep));
      } else if (e.deltaY > 0) {
        setValue((prev) => Math.max(-scaleMaxDiscount, prev - scrollStep));
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [open, scaleMaxMarkup, scaleMaxDiscount, rulerStep]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setValue((prev) => Math.min(scaleMaxMarkup, prev + 1));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setValue((prev) => Math.max(-scaleMaxDiscount, prev - 1));
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setValue((prev) => Math.min(scaleMaxMarkup, prev + rulerStep));
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setValue((prev) => Math.max(-scaleMaxDiscount, prev - rulerStep));
    }
  };

  const handleApply = () => {
    if (value > 0) {
      onApply(value, 0);
    } else if (value < 0) {
      onApply(0, Math.abs(value));
    } else {
      onApply(0, 0);
    }
  };

  const currentPointerY = getYFromValue(value);

  // Active status text and badge
  const isMarkup = value > 0;
  const isDiscount = value < 0;
  const activeMargin = isMarkup ? value : 0;
  const activeDiscount = isDiscount ? Math.abs(value) : 0;

  // -------------------------------------------------------------
  // LOCAL PRICING & IMPACT CALCULATION
  // -------------------------------------------------------------
  const {
    productBaseCost,
    accessoryBaseCost,
    replacementBaseCost,
    mezzanineBaseCost,
    hasItems,
  } = useMemo(() => {
    const origMargin = Number(
      activeQuotationDetails?.customer_snapshot?.margin ??
        activeQuotationDetails?.margin ??
        initialMargin ??
        0,
    );
    const origDiscount = Number(
      activeQuotationDetails?.customer_snapshot?.discount ??
        activeQuotationDetails?.discount ??
        activeQuotationDetails?.discount_percentage ??
        initialDiscount ??
        0,
    );

    const origMultiplier =
      (1 + (origMargin || 0) / 100) * (1 - (origDiscount || 0) / 100);
    const safeOrigMult = origMultiplier > 0 ? origMultiplier : 1;

    let pBase = 0;
    let aBase = 0;
    let rBase = 0;
    let foundItems = false;

    // 1. Check activeCartItems if available
    if (Array.isArray(activeCartItems) && activeCartItems.length > 0) {
      activeCartItems.forEach((item: any) => {
        const qty = Number(item.qty || item.quantity || 1);
        const itemCost = Number(item.rawPricing?.cost ?? item.cost ?? 0);
        const itemTotal = Number(item.rawPricing?.total ?? item.total ?? 0);
        const itemMargin = Number(
          item.rawPricing?.margin_percent ?? origMargin,
        );
        const itemDiscount = Number(
          item.rawPricing?.discount_percent ?? origDiscount,
        );
        const itemMult =
          (1 + (itemMargin || 0) / 100) * (1 - (itemDiscount || 0) / 100);

        const base =
          itemCost > 0
            ? itemCost * qty
            : itemMult > 0 && itemTotal > 0
            ? itemTotal / itemMult
            : itemTotal;

        if (base > 0 || itemTotal > 0) {
          foundItems = true;
        }

        if (item.itemType === "accessory") {
          aBase += base;
        } else if (item.itemType === "replacement_parts") {
          rBase += base;
        } else {
          pBase += base;
        }
      });
    }

    // 2. If not found in cartItems, extract from activeQuotationDetails.products
    if (!foundItems && activeQuotationDetails?.products) {
      const categories = Array.isArray(activeQuotationDetails.products)
        ? activeQuotationDetails.products
        : [activeQuotationDetails.products];

      let pSavedTotal = 0;
      let aSavedTotal = 0;
      let rSavedTotal = 0;

      categories.forEach((catGroup: any) => {
        if (catGroup.products_total_amount != null) {
          pSavedTotal += Number(catGroup.products_total_amount || 0);
        }
        if (catGroup.accessories_total_amount != null) {
          aSavedTotal += Number(catGroup.accessories_total_amount || 0);
        }
        if (catGroup.replacement_parts_total_amount != null) {
          rSavedTotal += Number(catGroup.replacement_parts_total_amount || 0);
        }

        const extractBaseFromList = (items: any[]) => {
          let sum = 0;
          (items || []).forEach((item: any) => {
            (item.variants || []).forEach((v: any) => {
              foundItems = true;
              const qty = Number(v.quantity || v.qty || 1);
              const cost = Number(v.variant_clone?.cost ?? v.cost ?? 0);
              const lineTotal = Number(v.line_total ?? v.total_price ?? 0);
              const vMargin = Number(v.margin_percent ?? origMargin);
              const vDiscount = Number(v.discount_percent ?? origDiscount);
              const vMult =
                (1 + (vMargin || 0) / 100) * (1 - (vDiscount || 0) / 100);

              if (cost > 0) {
                sum += cost * qty;
              } else if (vMult > 0 && lineTotal > 0) {
                sum += lineTotal / vMult;
              } else {
                sum += lineTotal;
              }
            });
          });
          return sum;
        };

        if (catGroup.products?.length > 0) {
          pBase += extractBaseFromList(catGroup.products);
        }
        if (catGroup.accessories?.length > 0) {
          aBase += extractBaseFromList(catGroup.accessories);
        }
        if (catGroup.replacement_parts?.length > 0) {
          rBase += extractBaseFromList(catGroup.replacement_parts);
        }
      });

      // Fallback to category group total amounts if variants were not provided
      if (pBase === 0 && pSavedTotal > 0) {
        pBase = pSavedTotal / safeOrigMult;
        foundItems = true;
      }
      if (aBase === 0 && aSavedTotal > 0) {
        aBase = aSavedTotal / safeOrigMult;
        foundItems = true;
      }
      if (rBase === 0 && rSavedTotal > 0) {
        rBase = rSavedTotal / safeOrigMult;
        foundItems = true;
      }
    }

    // Fallback directly to quotation details item totals if still not found
    if (!foundItems) {
      const pTot = Number(activeQuotationDetails?.products_total || 0);
      const aTot = Number(activeQuotationDetails?.accessories_total || 0);
      const rTot = Number(activeQuotationDetails?.replacement_parts_total || 0);
      if (pTot > 0 || aTot > 0 || rTot > 0) {
        pBase = pTot / safeOrigMult;
        aBase = aTot / safeOrigMult;
        rBase = rTot / safeOrigMult;
        foundItems = true;
      } else if (Number(activeQuotationDetails?.subtotal || 0) > 0) {
        pBase = Number(activeQuotationDetails.subtotal) / safeOrigMult;
        foundItems = true;
      }
    }

    let mBase = 0;
    if (activeQuotationDetails?.mezzanine_floor) {
      const mf = activeQuotationDetails.mezzanine_floor;
      const rawBase = Number(mf.base_cost || 0);
      if (rawBase > 0) {
        mBase = rawBase;
      } else if (Number(mf.total_cost || 0) > 0) {
        mBase = Number(mf.total_cost) / safeOrigMult;
      }
      if (mBase > 0 || Number(mf.total_cost || 0) > 0) {
        foundItems = true;
      }
    }

    return {
      productBaseCost: pBase,
      accessoryBaseCost: aBase,
      replacementBaseCost: rBase,
      mezzanineBaseCost: mBase,
      hasItems: foundItems,
    };
  }, [activeQuotationDetails, activeCartItems, initialMargin, initialDiscount]);

  // Current multiplier based on dynamic ruler position
  const currentMultiplier =
    (1 + (activeMargin || 0) / 100) * (1 - (activeDiscount || 0) / 100);

  // Initial multiplier for calculating difference
  const initMarginNum = Number(initialMargin) > 0 ? Number(initialMargin) : 0;
  const initDiscountNum =
    Number(initialDiscount) > 0 ? Number(initialDiscount) : 0;
  const initialMultiplier =
    (1 + (initMarginNum || 0) / 100) * (1 - (initDiscountNum || 0) / 100);

  // Dynamic live totals (calculated locally)
  const currentProductTotal =
    productBaseCost > 0 ? productBaseCost * currentMultiplier : 0;
  const currentAccessoryTotal =
    accessoryBaseCost > 0 ? accessoryBaseCost * currentMultiplier : 0;
  const currentReplacementTotal =
    replacementBaseCost > 0 ? replacementBaseCost * currentMultiplier : 0;
  const currentMezzanineTotal =
    mezzanineBaseCost > 0 ? mezzanineBaseCost * currentMultiplier : 0;
  const currentOverallTotal =
    currentProductTotal +
    currentAccessoryTotal +
    currentReplacementTotal +
    currentMezzanineTotal;

  // Initial totals
  const initialProductTotal =
    productBaseCost > 0 ? productBaseCost * initialMultiplier : 0;
  const initialAccessoryTotal =
    accessoryBaseCost > 0 ? accessoryBaseCost * initialMultiplier : 0;
  const initialReplacementTotal =
    replacementBaseCost > 0 ? replacementBaseCost * initialMultiplier : 0;
  const initialMezzanineTotal =
    mezzanineBaseCost > 0 ? mezzanineBaseCost * initialMultiplier : 0;
  const initialOverallTotal =
    initialProductTotal +
    initialAccessoryTotal +
    initialReplacementTotal +
    initialMezzanineTotal;

  // Differences
  const overallDiff = currentOverallTotal - initialOverallTotal;

  // Subtotal of full quote
  const quoteSavedSubtotal = Number(activeQuotationDetails?.subtotal || 0);
  const initialSubtotal =
    quoteSavedSubtotal > 0 ? quoteSavedSubtotal : initialOverallTotal;
  const currentSubtotal =
    initialSubtotal > 0 ? initialSubtotal + overallDiff : currentOverallTotal;
  const subtotalDiff = currentSubtotal - initialSubtotal;

  // VAT & Grand Total of full quote
  const vatPercentage = useMemo(() => {
    // 1. If VAT details are provided via props (e.g. from CustomerDetails form before saving)
    if (
      propVatPercentage !== undefined &&
      propVatPercentage !== null &&
      !isNaN(Number(propVatPercentage))
    ) {
      return Number(propVatPercentage);
    }

    // 2. From quotationDetails root or customer_snapshot
    if (
      activeQuotationDetails?.vat_percentage !== undefined &&
      activeQuotationDetails?.vat_percentage !== null
    ) {
      return Number(activeQuotationDetails.vat_percentage);
    }
    if (
      activeQuotationDetails?.customer_snapshot?.vat_percentage !== undefined &&
      activeQuotationDetails?.customer_snapshot?.vat_percentage !== null
    ) {
      return Number(activeQuotationDetails.customer_snapshot.vat_percentage);
    }
    return 0;
  }, [propVatPercentage, activeQuotationDetails]);

  const quoteSavedGrandTotal = Number(activeQuotationDetails?.grand_total || 0);
  const quoteSavedVatPercentage = Number(
    activeQuotationDetails?.vat_percentage ??
      activeQuotationDetails?.customer_snapshot?.vat_percentage ??
      0,
  );

  const initialVatAmount =
    vatPercentage <= 0
      ? 0
      : Math.abs(quoteSavedVatPercentage - vatPercentage) < 0.001 &&
        activeQuotationDetails?.vat_amount !== undefined &&
        activeQuotationDetails?.vat_amount !== null
        ? Number(activeQuotationDetails.vat_amount)
        : (initialSubtotal * vatPercentage) / 100;

  const initialGrandTotal =
    vatPercentage <= 0
      ? initialSubtotal
      : Math.abs(quoteSavedVatPercentage - vatPercentage) < 0.001 &&
        quoteSavedGrandTotal > 0
        ? quoteSavedGrandTotal
        : initialSubtotal + initialVatAmount;

  const currentVatAmount =
    vatPercentage > 0 ? (currentSubtotal * vatPercentage) / 100 : 0;
  const vatDiff = currentVatAmount - initialVatAmount;
  const currentGrandTotal = currentSubtotal + currentVatAmount;
  const grandTotalDiff = currentGrandTotal - initialGrandTotal;

  // Generate tick marks based on single step variable
  const halfStep = Math.max(1, Math.round(rulerStep / 2));

  // Markup ticks: from 1 to scaleMaxMarkup
  const markupTicks = [];
  for (let v = 1; v <= scaleMaxMarkup; v++) {
    const y = zeroY - v * unitHeight;
    const isMajor = v % rulerStep === 0 || v === scaleMaxMarkup;
    const isMedium = rulerStep > 2 && v % halfStep === 0 && !isMajor;
    markupTicks.push({
      value: v,
      y,
      isMajor,
      isMedium,
      isTerminal: v === scaleMaxMarkup,
    });
  }

  // Discount ticks: from 1 to scaleMaxDiscount
  const discountTicks = [];
  for (let v = 1; v <= scaleMaxDiscount; v++) {
    const y = zeroY + v * unitHeight;
    const isMajor = v % rulerStep === 0 || v === scaleMaxDiscount;
    const isMedium = rulerStep > 2 && v % halfStep === 0 && !isMajor;
    discountTicks.push({
      value: v,
      y,
      isMajor,
      isMedium,
      isTerminal: v === scaleMaxDiscount,
    });
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      closeIcon={svg?.closeIcon}
      className="custom-modal"
      wrapClassName="zn-1051"
      centered
      width={"50rem"}
      title={
        <div className="text-center fs-5 fw-bold lh-base">
          Adjust Markup / Discount
        </div>
      }
      footer={
        <div className="d-flex justify-content-center gap-2 gap-sm-4">
          <button
            type="button"
            className="btn btn-md btn-sm-lg btn-outline-secondary px-3 px-sm-4 min-w-100 min-w-sm-130"
            onClick={onCancel}
            disabled={isLoading}
          >
            CANCEL
          </button>
          <button
            onClick={handleApply}
            disabled={isLoading}
            className={`btn btn-md btn-sm-lg px-4 px-sm-5 min-w-100 min-w-sm-130 btn-primary`}
          >
            {isLoading ? <BtnLoader /> : "APPLY"}
          </button>
        </div>
        // <div className="d-flex justify-content-center gap-4">
        //   <Button
        //     onClick={onCancel}
        //     className="btn btn-lg btn-outline-secondary h-auto px-4"
        //     type="default"
        //     disabled={isLoading}
        //   >
        //     Cancel
        //   </Button>
        //   <Button
        //     onClick={handleApply}
        //     className="btn btn-lg btn-primary h-auto px-5"
        //     type="primary"
        //     disabled={isLoading}
        //   >
        //     {isLoading ? <BtnLoader /> : "Apply"}
        //   </Button>
        // </div>
      }
    >
      <div
        className="container-fluid px-0"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={{ outline: "none" }}
      >
        <div className="row g-2 g-md-4 align-items-center">
          {/* Left COLUMN: Real-Time Totals & Impact Panel */}
          <div className="col-7 col-md-6 col-lg-7 d-flex flex-column gap-2 gap-md-3">
            {/* Header & Active Selection Pill */}
            <div className="p-2 p-md-3 bg-light rounded-3 border d-flex flex-column gap-1 gap-md-2">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                <span className="fs-8 fs-md-7 text-primary fw-bold text-uppercase">
                  Adjustment
                </span>
                {isMarkup && (
                  <span className="badge bg-success fs-8 fs-md-7 px-2 px-md-3 py-1">
                    Markup : {activeMargin}%
                  </span>
                )}
                {isDiscount && (
                  <span className="badge bg-danger fs-8 fs-md-7 px-2 px-md-3 py-1">
                    Discount : {activeDiscount}%
                  </span>
                )}
                {!isMarkup && !isDiscount && (
                  <span className="badge bg-secondary fs-8 fs-md-7 px-2 px-md-3 py-1">
                    0% (Std)
                  </span>
                )}
              </div>

              {/* Quick Stepper Buttons using rulerStep */}
              <div className="d-flex align-items-center justify-content-center gap-1 pt-1 flex-wrap">
                <Tooltip title={`Decrease (-${rulerStep}% towards discount)`}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger px-1 px-md-2 py-0 py-md-1 fs-8 fs-md-7"
                    onClick={() =>
                      setValue((prev) =>
                        Math.max(-scaleMaxDiscount, prev - rulerStep),
                      )
                    }
                  >
                    -{rulerStep}%
                  </button>
                </Tooltip>
                <Tooltip title="Decrease (-1% towards discount)">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger px-1 px-md-2 py-0 py-md-1 fs-8 fs-md-7"
                    onClick={() =>
                      setValue((prev) => Math.max(-scaleMaxDiscount, prev - 1))
                    }
                  >
                    -1%
                  </button>
                </Tooltip>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    value === 0 ? "btn-secondary" : "btn-outline-secondary"
                  } px-2 px-md-3 py-0 py-md-1 fs-8 fs-md-7`}
                  onClick={() => setValue(0)}
                >
                  <span>
                    Reset
                    <span className="d-none d-md-inline"> to 0%</span>
                  </span>
                </button>
                <Tooltip title="Increase (+1% towards markup)">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success px-1 px-md-2 py-0 py-md-1 fs-8 fs-md-7"
                    onClick={() =>
                      setValue((prev) => Math.min(scaleMaxMarkup, prev + 1))
                    }
                  >
                    +1%
                  </button>
                </Tooltip>
                <Tooltip title={`Increase (+${rulerStep}% towards markup)`}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success px-1 px-md-2 py-0 py-md-1 fs-8 fs-md-7"
                    onClick={() =>
                      setValue((prev) =>
                        Math.min(scaleMaxMarkup, prev + rulerStep),
                      )
                    }
                  >
                    +{rulerStep}%
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* Live Impact Header */}
            <div className="d-flex align-items-center justify-content-between px-1">
              <span className="fs-7 fs-md-6 fw-bold text-dark">
                Live Impact Preview
              </span>
            </div>

            {/* Impact Cards Grid */}
            <div className="d-flex flex-column gap-2 gap-md-3">
              {/* Subtotal of Full Quote Card */}
              <div className="p-2 p-md-3 rounded-3 border bg-white shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-1">
                <div>
                  <div className="text-secondary fs-9 fs-md-8 fw-semibold text-uppercase">
                    Subtotal
                  </div>
                  <div className="fs-6 fs-md-5 fw-bold text-dark mt-0.5">
                    {formatCurrency(currentSubtotal, currency)}
                  </div>
                </div>
                {Math.abs(subtotalDiff) > 0.005 ? (
                  <div className="text-end">
                    <span
                      className={`badge fs-9 fs-md-8 px-1.5 px-md-2 py-0.5 py-md-1 ${
                        subtotalDiff > 0
                          ? "bg-success-subtle text-success border border-success"
                          : "bg-danger-subtle text-danger border border-danger"
                      }`}
                    >
                      {subtotalDiff > 0 ? "+" : ""}
                      {formatCurrency(subtotalDiff, currency)}
                    </span>
                    <div className="text-muted fs-9 fs-md-8 mt-0.5 d-none d-sm-block">
                      vs initial ({formatCurrency(initialSubtotal, currency)})
                    </div>
                  </div>
                ) : (
                  <span className="text-muted fs-9 fs-md-8">No change</span>
                )}
              </div>

              {/* VAT Card (if applicable) */}
              {(vatPercentage > 0 || currentVatAmount > 0) && (
                <div className="p-2 p-md-3 rounded-3 border bg-white shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-1">
                  <div>
                    <div className="d-flex align-items-center gap-1 gap-md-2">
                      <span className="text-secondary fs-9 fs-md-8 fw-semibold text-uppercase">
                        VAT
                      </span>
                      {vatPercentage > 0 && (
                        <span className="badge bg-light text-secondary border fs-9 fs-md-8 px-1 py-0.5">
                          {vatPercentage}%
                        </span>
                      )}
                    </div>
                    <div className="fs-6 fs-md-5 fw-bold text-dark mt-0.5">
                      {formatCurrency(currentVatAmount, currency)}
                    </div>
                  </div>
                  {Math.abs(vatDiff) > 0.005 ? (
                    <div className="text-end">
                      <span
                        className={`badge fs-9 fs-md-8 px-1.5 px-md-2 py-0.5 py-md-1 ${
                          vatDiff > 0
                            ? "bg-success-subtle text-success border border-success"
                            : "bg-danger-subtle text-danger border border-danger"
                        }`}
                      >
                        {vatDiff > 0 ? "+" : ""}
                        {formatCurrency(vatDiff, currency)}
                      </span>
                      <div className="text-muted fs-9 fs-md-8 mt-0.5 d-none d-sm-block">
                        vs initial ({formatCurrency(initialVatAmount, currency)}
                        )
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted fs-9 fs-md-8">No change</span>
                  )}
                </div>
              )}

              {/* Grand Total of Full Quote Card */}
              <div
                className="p-2 p-md-3 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-1"
                style={{ backgroundColor: "#F8FAFC", borderColor: "#CBD5E1" }}
              >
                <div>
                  <div className="d-flex align-items-center gap-1 gap-md-2">
                    <span className="text-dark fs-8 fs-md-7 fw-bolder text-uppercase">
                      Grand Total
                    </span>
                    {vatPercentage > 0 && (
                      <span className="badge bg-light text-secondary border fs-9 fs-md-8 px-1 py-0.5 d-none d-sm-inline-block">
                        Incl. {vatPercentage}% VAT
                      </span>
                    )}
                  </div>
                  <div className="fs-5 fs-md-4 fw-bolder text-primary mt-0.5">
                    {formatCurrency(currentGrandTotal, currency)}
                  </div>
                </div>
                {Math.abs(grandTotalDiff) > 0.005 ? (
                  <div className="text-end">
                    <span
                      className={`badge fs-8 fs-md-7 px-1.5 px-md-2 py-0.5 py-md-1 ${
                        grandTotalDiff > 0
                          ? "bg-success text-white"
                          : "bg-danger text-white"
                      }`}
                    >
                      {grandTotalDiff > 0 ? "+" : ""}
                      {formatCurrency(grandTotalDiff, currency)}
                    </span>
                    <div className="text-muted fs-9 fs-md-8 mt-0.5 d-none d-sm-block">
                      vs initial ({formatCurrency(initialGrandTotal, currency)})
                    </div>
                  </div>
                ) : (
                  <span className="text-muted fs-9 fs-md-8">Baseline</span>
                )}
              </div>
            </div>

            {!hasItems && (
              <div className="p-2 text-center text-muted fs-9 fs-md-8 rounded bg-light border">
                No items found for price adjustment.
              </div>
            )}
          </div>
          {/* Right COLUMN: Vertical Ruler Scale */}
          <div className="col-5 col-md-6 col-lg-5 d-flex flex-column align-items-center">
            <div
              ref={rulerContainerRef}
              className="position-relative user-select-none d-flex justify-content-center"
              style={{
                cursor: isDragging ? "grabbing" : "pointer",
                width: "100%",
                maxHeight: "580px",
                overflow: "hidden",
                touchAction: "none",
                overscrollBehavior: "contain",
                WebkitUserSelect: "none",
                userSelect: "none",
                padding: "2px 0",
              }}
              onMouseDown={handlePointerDown}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                style={{
                  width: "100%",
                  maxWidth: "180px",
                  height: "auto",
                  display: "block",
                  touchAction: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }}
              >
                {/* Top Label: Markup (Increases Price) */}
                <text
                  x={centerX}
                  y={26}
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="900"
                  fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                  fill="#000000"
                >
                  Markup
                </text>
                <text
                  x={centerX}
                  y={44}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                  fill="#111827"
                >
                  (Increases Price)
                </text>

                {/* Top crossbar at max markup */}
                <line
                  x1={centerX - 10}
                  y1={rulerTopY}
                  x2={centerX + 10}
                  y2={rulerTopY}
                  stroke="#000000"
                  strokeWidth="3"
                  strokeLinecap="square"
                />

                {/* Bottom crossbar at max discount */}
                <line
                  x1={centerX - 10}
                  y1={rulerBottomY}
                  x2={centerX + 10}
                  y2={rulerBottomY}
                  stroke="#000000"
                  strokeWidth="3"
                  strokeLinecap="square"
                />

                {/* Vertical Center Axis */}
                <line
                  x1={centerX}
                  y1={rulerTopY}
                  x2={centerX}
                  y2={rulerBottomY}
                  stroke="#000000"
                  strokeWidth="3"
                  strokeLinecap="square"
                />

                {/* Markup Ticks and Labels */}
                {markupTicks.map((t) => (
                  <g key={`markup-${t.value}`}>
                    <line
                      x1={centerX}
                      y1={t.y}
                      x2={centerX + (t.isMajor ? 14 : t.isMedium ? 9 : 5)}
                      y2={t.y}
                      stroke="#000000"
                      strokeWidth={
                        t.isMajor ? "2.5" : t.isMedium ? "1.8" : "1.2"
                      }
                    />
                    {t.isMajor && (
                      <text
                        x={centerX + 18}
                        y={t.y + 1}
                        textAnchor="start"
                        dominantBaseline="middle"
                        fontSize="14"
                        fontWeight="700"
                        fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                        fill="#000000"
                      >
                        {t.value}
                      </text>
                    )}
                  </g>
                ))}

                {/* Zero Tick and Label */}
                <g key="tick-0">
                  <line
                    x1={centerX}
                    y1={zeroY}
                    x2={centerX + 14}
                    y2={zeroY}
                    stroke="#000000"
                    strokeWidth="2.5"
                  />
                  <text
                    x={centerX + 18}
                    y={zeroY + 1}
                    textAnchor="start"
                    dominantBaseline="middle"
                    fontSize="14"
                    fontWeight="700"
                    fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                    fill="#000000"
                  >
                    0
                  </text>
                </g>

                {/* Discount Ticks and Labels */}
                {discountTicks.map((t) => (
                  <g key={`discount-${t.value}`}>
                    <line
                      x1={centerX}
                      y1={t.y}
                      x2={centerX + (t.isMajor ? 14 : t.isMedium ? 9 : 5)}
                      y2={t.y}
                      stroke="#000000"
                      strokeWidth={
                        t.isMajor ? "2.5" : t.isMedium ? "1.8" : "1.2"
                      }
                    />
                    {t.isMajor && (
                      <text
                        x={centerX + 18}
                        y={t.y + 1}
                        textAnchor="start"
                        dominantBaseline="middle"
                        fontSize="14"
                        fontWeight="700"
                        fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                        fill="#000000"
                      >
                        {t.value}
                      </text>
                    )}
                  </g>
                ))}

                {/* Pointer / Cursor Shape matching the attached image */}
                <g
                  transform={`translate(0, 0)`}
                  style={{
                    cursor: isDragging ? "grabbing" : "grab",
                    filter: isDragging
                      ? "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))"
                      : "none",
                    transition: isDragging ? "none" : "all 0.05s ease-out",
                  }}
                >
                  {/* Tail line */}
                  <line
                    x1={centerX - 38}
                    y1={currentPointerY}
                    x2={centerX - 24}
                    y2={currentPointerY}
                    stroke="#000000"
                    strokeWidth="2.5"
                  />
                  {/* Pointer box + arrow tip pointing right to centerX */}
                  <path
                    d={`M ${centerX - 24} ${currentPointerY - 10} L ${
                      centerX - 12
                    } ${
                      currentPointerY - 10
                    } L ${centerX} ${currentPointerY} L ${centerX - 12} ${
                      currentPointerY + 10
                    } L ${centerX - 24} ${currentPointerY + 10} Z`}
                    fill={
                      value > 0 ? "#E8F5E9" : value < 0 ? "#FFEBEE" : "#FFFFFF"
                    }
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinejoin="miter"
                  />
                </g>

                {/* Bottom Label: Discount (Decreases Price) */}
                <text
                  x={centerX}
                  y={rulerBottomY + 30}
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="900"
                  fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                  fill="#000000"
                >
                  Discount
                </text>
                <text
                  x={centerX}
                  y={rulerBottomY + 48}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                  fill="#111827"
                >
                  (Decreases Price)
                </text>
              </svg>
            </div>
            <div className="text-center text-muted fs-9 fs-sm-8 mt-1">
              Scroll or drag ruler to adjust
            </div>
          </div>
        </div>

        {/* Modal Actions */}
      </div>
    </Modal>
  );
};

export default MarginDiscountModal;
